import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { clubAPI, uploadAPI } from '../services/api';
import MapPicker from '../components/MapPicker';
import { useAlert } from '../components/Alert';
import './CreateLeague.css';

const EditClub = () => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showAlert } = useAlert();

  const [formData, setFormData] = useState({
    name: '',
    sport: 'Tennis (singles)',
    latitude: null,
    longitude: null,
    logo: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadClub = async () => {
      try {
        setLoading(true);
        const response = await clubAPI.getClub(clubId);
        const club = response.data;

        // Only allow access if user is club admin or global admin
        if (!club.isAdmin && !club.isGlobalAdmin) {
          showAlert('You do not have permission to edit this club.', 'error');
          navigate(`/clubs/${clubId}`);
          return;
        }

        setFormData({
          name: club.name || '',
          sport: club.sport || 'Tennis (singles)',
          latitude: club.latitude ?? null,
          longitude: club.longitude ?? null,
          logo: club.logo || '',
        });
      } catch (err) {
        console.error('Failed to load club for editing:', err);
        setError('Failed to load club');
      } finally {
        setLoading(false);
      }
    };

    if (clubId && user) {
      loadClub();
    }
  }, [clubId, user, navigate, showAlert]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setLogoFile(file);
  };

  const handleLocationSelect = (position) => {
    setFormData((prev) => ({
      ...prev,
      latitude: position.lat,
      longitude: position.lng,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      showAlert('Club name is required', 'warning');
      return;
    }

    if (!formData.latitude || !formData.longitude) {
      showAlert('Please select a location on the map', 'warning');
      return;
    }

    try {
      setSaving(true);

      // Update basic fields
      await clubAPI.updateClub(clubId, {
        name: formData.name.trim(),
        sport: formData.sport,
        latitude: formData.latitude,
        longitude: formData.longitude,
        logo: formData.logo || '',
      });

      // Optional: upload new logo
      if (logoFile) {
        try {
          const uploadRes = await uploadAPI.uploadClubLogo(logoFile, clubId);
          const logoUrl = uploadRes.data.url;
          await clubAPI.updateClub(clubId, { logo: logoUrl });
        } catch (uploadErr) {
          console.warn('Club logo upload failed:', uploadErr);
        }
      }

      showAlert('Club updated successfully.', 'success');
      navigate(`/clubs/${clubId}`);
    } catch (err) {
      console.error('Update club error:', err);
      setError(err.response?.data?.error || 'Failed to update club');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="container">
          <div className="loading">Loading club...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="container">
          <div className="error-message">{error}</div>
          <button
            onClick={() => navigate(-1)}
            className="btn btn-secondary"
            style={{ marginTop: '16px' }}
          >
            ← Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="container">
        <div className="page-header">
          <button
            onClick={() => navigate(-1)}
            className="btn btn-secondary"
            style={{ marginBottom: '16px' }}
          >
            ← Back
          </button>
          <h1 className="page-title">Edit Club</h1>
          <p className="page-subtitle">Update the details and location of your club</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="create-league-card card">
          <form onSubmit={handleSubmit} className="create-league-form">
            <div className="form-row three-columns">
              <div className="form-group">
                <label className="form-label">Club Name *</label>
                <input
                  type="text"
                  name="name"
                  className="form-input"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., Central Tennis Club"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Sport *</label>
                <select
                  name="sport"
                  className="form-input sport-select"
                  value={formData.sport}
                  onChange={handleChange}
                  required
                >
                  <option value="Tennis (singles)">🎾 Tennis (singles)</option>
                  <option value="Tennis (doubles)" disabled>🎾 Tennis (doubles) (Coming Soon)</option>
                  <option value="Padel" disabled>🏓 Padel (Coming Soon)</option>
                  <option value="Squash">🏸 Squash</option>
                  <option value="Football" disabled>⚽ Football (Coming Soon)</option>
                  <option value="Basketball" disabled>🏀 Basketball (Coming Soon)</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Current Logo</label>
                {formData.logo ? (
                  <div style={{ marginBottom: '12px' }}>
                    <img
                      src={formData.logo}
                      alt="Club logo"
                      style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px' }}
                    />
                  </div>
                ) : (
                  <small className="form-hint">No logo uploaded yet.</small>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Update Logo (optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  className="form-input"
                  onChange={handleLogoChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Location on Map *</label>
              <MapPicker
                onLocationSelect={handleLocationSelect}
                initialLocation={
                  formData.latitude && formData.longitude
                    ? { lat: formData.latitude, lng: formData.longitude }
                    : null
                }
              />
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={() => navigate(`/clubs/${clubId}`)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditClub;


